<template>
  <v-row class="ma-0 pa-0" align="center" justify="center">
    <v-col cols="12" class="chart_style ma-0 pa-0">
      <v-chart
        :id="chart_id"
        :ref="chart_ref"
        class="chart"
        :option="option"
        autoresize
      ></v-chart>
    </v-col>
  </v-row>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LinesChart, ScatterChart } from "echarts/charts";
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";

use([
  CanvasRenderer,
  BarChart,
  LinesChart,
  ScatterChart,
  GridComponent,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
]);

const VALUE_KEYS = [
  "intersection_reach_pop",
  "value",
  "count",
  "size",
  "total",
  "reach",
  "overlap",
  "intersection_reach",
];
const GROUPING_KEYS = ["sets", "items", "grouping", "groups", "members", "combination"];
const LABEL_KEYS = ["combo_lbl", "label", "name", "title"];
const GROUP_COLORS = [
  "#70E000", "#A4133C", "#2AB7CA", "#3A0CA3", "#F6BD60", "#1F4E5F", "#F28482",
  "#2F5D50", "#9FE0D0", "#A44A3F", "#CDB4DB", "#D7263D", "#75B9E3", "#6B8E23",
  "#E8B4B8", "#1E3A8A", "#F77F00", "#6D597A", "#A7C957", "#D0006F", "#B7D3E9",
  "#4A6F46", "#F4A261", "#2E5AAC", "#9CAF88", "#7B2CBF", "#C19A6B", "#00B4D8",
  "#C08497", "#2A9D8F", "#E9C46A", "#3A86C8", "#B46A55", "#4EC5C1", "#D4A017",
  "#A2678A", "#D8C3A5", "#C06542", "#B8A1D9", "#D6E6F2"
];

function normalizeGrouping(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.keys(value).filter((key) => Boolean(value[key]));
  }
  if (typeof value === "string") {
    return value
      .split(/\s*(?:\+|,|&|\||\u2229)\s*/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function rowArrayToObject(columns, row) {
  return columns.reduce((output, column, index) => {
    output[column] = row[index];
    return output;
  }, {});
}

function pickFirst(row, keys) {
  for (let key of keys) {
    if (row && row[key] != null) {
      return row[key];
    }
  }
  return null;
}

function formatShortNumber(value) {
  const numericValue = Number(value);
  const absValue = Math.abs(numericValue);

  if (!Number.isFinite(numericValue)) {
    return "0";
  }
  if (absValue >= 1000000000) {
    return `${(numericValue / 1000000000).toFixed(absValue >= 10000000000 ? 0 : 1).replace(/\.0$/, "")}B`;
  }
  if (absValue >= 1000000) {
    return `${(numericValue / 1000000).toFixed(absValue >= 10000000 ? 0 : 1).replace(/\.0$/, "")}M`;
  }
  if (absValue >= 1000) {
    return `${(numericValue / 1000).toFixed(absValue >= 10000 ? 0 : 1).replace(/\.0$/, "")}K`;
  }
  return numericValue.toFixed(absValue >= 10 ? 0 : 1).replace(/\.0$/, "");
}

function getGroupColor(groupSize) {
  return GROUP_COLORS[(groupSize - 1) % GROUP_COLORS.length];
}

function normalizeRows(chartData) {
  if (
    chartData &&
    Array.isArray(chartData.columns) &&
    Array.isArray(chartData.rows)
  ) {
    return chartData.rows.map((row) => rowArrayToObject(chartData.columns, row));
  }
  if (Array.isArray(chartData)) {
    return chartData;
  }
  if (chartData && Array.isArray(chartData.data)) {
    return chartData.data;
  }
  if (chartData && Array.isArray(chartData.intersections)) {
    return chartData.intersections;
  }
  if (chartData && typeof chartData === "object") {
    return Object.keys(chartData).map((key) => ({
      name: key,
      value: chartData[key],
      grouping: key,
    }));
  }
  return [];
}

function buildPlotData(chartData) {
  const rows = normalizeRows(chartData);
  const intersections = rows
    .map((row) => {
      const groupingFromComboLabel = normalizeGrouping(row.combo_lbl);
      const setColumns = Object.keys(row || {}).filter((key) => key.indexOf("set_") === 0);
      const groupingFromSetColumns = setColumns
        .filter((key) => Boolean(row[key]))
        .map((key) => key.replace(/^set_/, "").replace(/_/g, " "));
      const grouping = (
        groupingFromComboLabel.length > 0
          ? groupingFromComboLabel
          : normalizeGrouping(row.combo_members || pickFirst(row, GROUPING_KEYS)).concat(groupingFromSetColumns)
      )
        .filter((item, index, all) => all.indexOf(item) === index);
      const rawValue = pickFirst(row, VALUE_KEYS);
      const value = Number(rawValue);
      const reach = Number(row.intersection_reach);
      const label = pickFirst(row, LABEL_KEYS) || grouping.join(" + ");

      return {
        label: String(label || ""),
        grouping,
        groupSize: Number(row.combo_size) || grouping.length,
        value: Number.isFinite(value) ? value : 0,
        reach: Number.isFinite(reach) ? reach : null,
      };
    })
    .filter((row) => row.grouping.length > 0)
    .sort((a, b) => a.groupSize - b.groupSize || b.value - a.value || a.label.localeCompare(b.label));

  const setNames = Array.from(
    new Set(intersections.reduce((all, row) => all.concat(row.grouping), []))
  ).sort();

  return { intersections, setNames };
}

export default {
  name: "UpsetPlot",
  props: ["chart_title", "chart_data", "chart_id", "chart_ref"],
  components: {
    VChart,
  },
  provide: {
    [THEME_KEY]: "light",
  },
  computed: {
    option: function () {
      const { intersections, setNames } = buildPlotData(this.chart_data);
      const xLabels = intersections.map((row) => row.label);
      const barData = intersections.map((row) => ({
        value: row.value,
        itemStyle: {
          color: getGroupColor(row.groupSize),
        },
      }));
      const scatterData = [];
      const lineData = [];

      intersections.forEach((row, index) => {
        const color = getGroupColor(row.groupSize);
        const memberIndexes = row.grouping
          .map((name) => setNames.indexOf(name))
          .filter((idx) => idx > -1);

        row.grouping.forEach((name) => {
          scatterData.push({
            value: [index, name],
            groupSize: row.groupSize,
            intersection: row.label,
            count: row.value,
            itemStyle: {
              color,
            },
          });
        });

        if (memberIndexes.length > 1) {
          lineData.push({
            coords: [
              [index, setNames[Math.min(...memberIndexes)]],
              [index, setNames[Math.max(...memberIndexes)]],
            ],
            lineStyle: {
              color,
              width: 2,
            },
          });
        }
      });

      return {
        title: {
          text: this.chart_title,
          fontFamily: "Poppins",
          left: "center",
        },
        tooltip: {
          trigger: "item",
          formatter: function (params) {
            const rowIndex = Array.isArray(params.value) ? params.value[0] : params.dataIndex;
            const row = intersections[rowIndex];
            if (!row) {
              return "";
            }
            return [
              `<strong>${row.label}</strong>`,
              `Items in grouping: ${row.groupSize}`,
              `Reach Pop: ${formatShortNumber(row.value)}`,
              row.reach == null ? "" : `Reach: ${(row.reach * 100).toFixed(4)}%`,
            ].filter(Boolean).join("<br/>");
          },
        },
        grid: [
          {
            left: 180,
            right: 36,
            top: 56,
            height: 210,
            containLabel: false,
          },
          {
            left: 180,
            right: 36,
            top: 326,
            bottom: 118,
            containLabel: false,
          },
        ],
        xAxis: [
          {
            type: "category",
            data: xLabels,
            axisLabel: {
              show: false,
            },
            axisTick: {
              show: false,
            },
          },
          {
            type: "category",
            gridIndex: 1,
            data: xLabels,
            axisLabel: {
              fontSize: 11,
              fontFamily: "Poppins",
              show: false,
            },
            axisTick: {
              show: false,
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            name: "Reach Pop",
            nameTextStyle: {
              fontFamily: "Poppins",
            },
            axisLabel: {
              fontFamily: "Poppins",
              formatter: formatShortNumber,
            },
          },
          {
            type: "category",
            gridIndex: 1,
            data: setNames,
            axisLabel: {
              fontSize: 12,
              fontFamily: "Poppins",
            },
          },
        ],
        series: [
          {
            name: "Overlap",
            type: "bar",
            data: barData,
            label: {
              show: true,
              position: "top",
              fontFamily: "Poppins",
              fontSize: 11,
              formatter: function (params) {
                return formatShortNumber(params.value);
              },
            },
          },
          {
            name: "Grouping Connector",
            type: "lines",
            coordinateSystem: "cartesian2d",
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: lineData,
            symbol: "none",
            lineStyle: {
              color: "#333333",
              width: 2,
            },
            silent: true,
          },
          {
            name: "Grouping",
            type: "scatter",
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: scatterData,
            symbolSize: 11,
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.chart_style {
  height: 720px;
  font-family: "Poppins" !important;
}
</style>
